import type { Resource } from '@medplum/fhirtypes';
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import {
  type CommitResult,
  type FhirWriteTransport,
  classifyWriteError,
} from '../data/write-transport';

import {
  FhirEditStore,
  createFhirDraft,
  diffResourceOps,
  draftPayload,
  hasDraftId,
} from './edit-store';

type EditScopeValue = {
  active: boolean;
  store: FhirEditStore;
  transport?: FhirWriteTransport;
};

const EditScopeContext = createContext<EditScopeValue | undefined>(undefined);

/** Internal: FhirResourceScope reads this to stamp ctx.mode on the tree. */
export function useEditScopeMode(): 'read' | 'edit' {
  return useContext(EditScopeContext)?.active ? 'edit' : 'read';
}

export type FhirEditScopeProps = {
  /** When false the subtree renders read-only; edits are kept until reset. */
  active?: boolean;
  /**
   * Write transport (a MedplumClient satisfies it). Commit fails with a
   * network error when absent — reads never require write wiring.
   */
  transport?: FhirWriteTransport;
  children: ReactNode;
};

/**
 * Edit-mode boundary. Everything the walker renders inside an active scope
 * gets `ctx.mode === 'edit'`; edit variants bind fields through
 * useFhirEditState, and the app's save affordance drives useFhirEditScope
 * (dirty count, commit, reset). The renderer itself never writes — commit
 * diffs each dirty resource into a JSONPatch (with a versionId test op) and
 * hands it to the transport.
 */
export function FhirEditScope({
  active = true,
  transport,
  children,
}: FhirEditScopeProps) {
  const storeRef = useRef<FhirEditStore>(undefined);
  storeRef.current ??= new FhirEditStore();
  const store = storeRef.current;
  const value = useMemo(
    () => ({ active, store, transport }),
    [active, store, transport],
  );
  return (
    <EditScopeContext.Provider value={value}>
      {children}
    </EditScopeContext.Provider>
  );
}

function useEditScopeContext(caller: string): EditScopeValue {
  const value = useContext(EditScopeContext);
  if (!value) {
    throw new Error(`[fhir-renderer] ${caller} requires a <FhirEditScope>`);
  }
  return value;
}

export type FhirEditScopeHandle = {
  active: boolean;
  /** Resources with uncommitted changes. */
  dirtyCount: number;
  /** Patch every dirty resource; clears the edits that committed. */
  commit: () => Promise<CommitResult>;
  /** Drop all edits (cancel). */
  reset: () => void;
};

/** The app-side handle — save bars, dirty badges, cancel buttons. */
export function useFhirEditScope(): FhirEditScopeHandle {
  const { active, store, transport } = useEditScopeContext('useFhirEditScope');
  const version = useSyncExternalStore(store.subscribe, store.getStoreVersion);

  const commit = useCallback(async (): Promise<CommitResult> => {
    const dirty = store.dirtyEntries();
    if (!transport) {
      return {
        ok: false,
        failures: dirty.map(({ key }) => ({
          key,
          error: {
            kind: 'network' as const,
            message: 'No write transport configured on this FhirEditScope.',
          },
        })),
      };
    }
    const failures: Array<{
      key: string;
      error: ReturnType<typeof classifyWriteError>;
    }> = [];
    let committed = 0;
    for (const { key, original, current, draft } of dirty) {
      if (!current.id) {
        // An edited copy without an id can be neither patched nor keyed —
        // report it; silently skipping would leave Save stuck dirty.
        failures.push({
          key,
          error: {
            kind: 'invalid' as const,
            message: 'Edited copy lost its resource id; cannot commit.',
          },
        });
        continue;
      }
      if (!draft && hasDraftId(current)) {
        // Draft entry was committed and re-edited without remounting the
        // host — patching the synthetic id would 404 as a misleading
        // network error. Surface the real problem instead.
        failures.push({
          key,
          error: {
            kind: 'invalid' as const,
            message:
              'This draft was already saved; remount the draft host (fresh key) to create another.',
          },
        });
        continue;
      }
      try {
        if (draft) {
          // Draft → POST without the synthetic id.
          await transport.createResource(draftPayload(current));
        } else {
          const ops = diffResourceOps(original, current);
          await transport.patchResource(current.resourceType, current.id, ops);
        }
        store.revert(original);
        committed += 1;
      } catch (error) {
        failures.push({ key, error: classifyWriteError(error) });
      }
    }
    return failures.length > 0
      ? { ok: false, failures }
      : { ok: true, committed };
  }, [store, transport]);

  const reset = useCallback(() => store.reset(), [store]);

  // version is the store's change signal — dirtyCount derives from it.
  return useMemo(
    () => ({ active, dirtyCount: store.dirtyCount(), commit, reset }),
    [active, commit, reset, store, version],
  );
}

export type FhirEditState<T extends Resource> = {
  /** The edited copy — render inputs from this, never from `data`. */
  current: T;
  setCurrent: (next: T) => void;
  dirty: boolean;
  revert: () => void;
};

/**
 * Field binding for edit variants: `current` starts as the server resource
 * and tracks edits; `setCurrent` takes a whole updated copy (immutable
 * update). Subscription is per-resource, so keystrokes re-render only the
 * view being edited.
 */
/**
 * Create-and-register a draft in the enclosing edit scope. The draft is
 * stable for the component's lifetime and marked in the scope's store, so
 * commit POSTs it (id stripped) instead of patching. Unmounting before a
 * successful commit drops the pending draft — cancel semantics, wherever
 * the draft lives (dialog, inline section, side panel). A successful commit
 * consumes the store entry: remount the draft host (fresh key) to start the
 * next draft rather than reusing this one.
 */
export function useFhirDraft<T extends Resource>(seed: () => Omit<T, 'id'>): T {
  const { store } = useEditScopeContext('useFhirDraft');
  const [draft] = useState(() => createFhirDraft<T>(seed()));
  useEffect(() => {
    store.registerDraft(draft);
    return () => store.revert(draft);
  }, [store, draft]);
  return draft;
}

export function useFhirEditState<T extends Resource>(
  resource: T,
): FhirEditState<T> {
  const { store } = useEditScopeContext('useFhirEditState');
  const subscribe = useCallback(
    (listener: () => void) => store.subscribeToResource(resource, listener),
    [store, resource],
  );
  const current = useSyncExternalStore(subscribe, () =>
    store.currentFor(resource),
  );
  const setCurrent = useCallback(
    (next: T) => store.setCurrent(resource, next),
    [store, resource],
  );
  const revert = useCallback(() => store.revert(resource), [store, resource]);
  return {
    current,
    setCurrent,
    dirty: store.isDirty(resource),
    revert,
  };
}
